import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { tasks } from "../../lib/api";
import { useUI } from "../../lib/store";
import { Modal } from "../shared/Modal";
import { Button } from "../shared/Button";
import { TaskFields, type TaskFieldsValue } from "./TaskFields";

interface Props {
  onClose: () => void;
}

export function NewTaskForm({ onClose }: Props) {
  const qc = useQueryClient();
  const selectedProjectId = useUI((s) => s.selectedProjectId);
  const setSelectedTask = useUI((s) => s.setSelectedTaskId);
  const setRightPaneMode = useUI((s) => s.setRightPaneMode);

  const [fields, setFields] = useState<TaskFieldsValue>({
    projectId: selectedProjectId ?? "",
    title: "",
    description: "",
    prompt: "",
    autonomy: "",
    model: "",
    priority: "",
    specMode: false,
    tier: "normal",
    acceptanceCriteria: "",
  });
  const create = useMutation({
    mutationFn: async () => {
      const created = await tasks.create({
        project_id: fields.projectId,
        title: fields.title,
        description: fields.description || null,
        prompt: fields.prompt || null,
        status: "todo",
        autonomy_level: fields.autonomy || undefined,
        model: fields.model || undefined,
        priority: fields.priority || undefined,
        spec_mode: fields.specMode || undefined,
        tier: fields.tier,
        acceptance_criteria: fields.acceptanceCriteria || null,
      });
      return created;
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["kanban"] });
      setSelectedTask(created.task.id);
      setRightPaneMode("activity");
      const sync = created.pending_sync;
      if (sync && sync.status !== "ok" && sync.status !== "skipped") {
        // Surface a non-blocking warning when the worker-side append didn't
        // land cleanly. "skipped" is expected when autonomy forbids commits
        // or the slug already exists — no UI noise needed there.
        // eslint-disable-next-line no-alert
        alert(
          `Task created, but PENDING.md was not updated cleanly:\n\n${sync.message}`,
        );
      }
      onClose();
    },
  });

  const canSubmit = fields.title && fields.projectId && !create.isPending;

  return (
    <Modal
      title="New task"
      onClose={onClose}
      footer={
        <>
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Saving…" : "Create"}
          </Button>
        </>
      }
    >
      <div>
        <TaskFields
          value={fields}
          onChange={(patch) => setFields((f) => ({ ...f, ...patch }))}
        />
        {create.isError && (
          <div className="mt-3 text-small text-status-error">
            {String(
              (create.error as { message?: string }).message ??
                "Failed to create task",
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
